/**
 * Performance utilities for measuring and tracking application performance
 */

/**
 * Gets performance navigation timing data if supported by the browser
 * @returns PerformanceNavigationTiming object or null if not supported
 */
export const getPerformanceNavigationTiming =
  (): PerformanceNavigationTiming | null => {
    try {
      if (
        window.performance &&
        'getEntriesByType' in window.performance &&
        window.performance.getEntriesByType
      ) {
        const navigationEntries =
          window.performance.getEntriesByType('navigation')
        if (navigationEntries.length > 0) {
          return navigationEntries[0] as PerformanceNavigationTiming
        }
      }
      return null
    } catch (error) {
      console.error('Error accessing performance metrics:', error)
      return null
    }
  }

/**
 * Measures the time to first contentful paint
 * @returns Time to first contentful paint in milliseconds, or null if not available
 */
export const getFirstContentfulPaint = (): number | null => {
  try {
    if (
      window.performance &&
      'getEntriesByType' in window.performance &&
      window.performance.getEntriesByType
    ) {
      const paintMetrics = window.performance.getEntriesByType('paint')
      const fcpMetric = paintMetrics.find(
        (entry) => entry.name === 'first-contentful-paint'
      )

      return fcpMetric ? fcpMetric.startTime : null
    }
    return null
  } catch (error) {
    console.error('Error accessing paint metrics:', error)
    return null
  }
}

/**
 * Measures time to interactive (approximate)
 * @returns Time to interactive in milliseconds
 */
export const measureTimeToInteractive = (): number => {
  const navigationEntry = getPerformanceNavigationTiming()

  if (navigationEntry) {
    // Approximation based on domInteractive plus a small delay
    return navigationEntry.domInteractive
  }

  // Fallback if performance API not available
  const pageStart =
    (window as Window & { pageStartTime?: number }).pageStartTime || 0
  return Date.now() - pageStart
}

/**
 * Checks if the browser supports all required performance APIs
 * @returns Boolean indicating if all required APIs are supported
 */
export const hasPerformanceSupport = (): boolean => {
  return Boolean(
    window.performance &&
      'getEntriesByType' in window.performance &&
      typeof window.performance.getEntriesByType === 'function'
  )
}
